import { SmileOutlined } from '@ant-design/icons';
import Picker from '@emoji-mart/react';
import {
  Alert,
  Button,
  Divider,
  Flex,
  Form,
  Space,
  Tooltip,
  Typography,
  Input,
} from 'antd';
import cn from 'classnames';

import { KeyWords } from '@app/components/ExtendedMessage/components/KeyWords';
import { MessagesPreviewWindow } from '@app/components/ExtendedMessage/components/MessagesPreviewWindow';
import type { NExtendedMessage } from '@app/components/ExtendedMessage/ExtendedMessage.types';
import { MessageTemplatesWrapper } from '@app/components/MessageTemplatesWrapper/MessageTemplatesWrapper.main';
import { LOYALTY_PROGRAM_SENDOUTS, PREFIX } from '@app/constants';
import { useExtendedMessage } from '@app/hooks';

import '@app/components/ExtendedMessage/styles/ExtendedMessage.desktop.scss';

const { Text } = Typography;
const { TextArea } = Input;

export const ExtendedMessage = ({
  form,
  formItemName,
  socialType,
  selectedClientIds,
  formItemClassName,
  formItemRules,
  messageFieldStyles,
  className = '',
  disabled = false,
  withTemplateOffers = false,
  messageTextSize = 'middle',
  placeholder = 'Поле для ввода сообщения...',
  autoSize = {},
  onChange,
  textAreaDataTestId = '',
  previewButtonDataTestId = '',
  emojiButtonDataTestId = '',
  keywordsButtonDataTestId = '',
}: NExtendedMessage.MainComponentProps) => {
  const {
    isMessagesPreviewModalOpen,
    props,
    isKeyWordsOpen,
    previewMessages,
    isEmojiOpen,
    pickerRef,
    emojiVisibilityButtonRef,
    messageFieldRef,
    isKeywordsLoading,
    sendoutKeywords,
    maxLength,
    messageSymbolsCount,
    isMoreThanSymbolsMax,
    isLeadsService,
    isMessageTemplatesService,
    toggleEmojiPicker,
    setIsKeyWordsOpen,
    onEmojiSelect,
    addExtraTextToMessage,
    getDescriptionTag,
    setIsMessagesPreviewModalOpen,
    handleKeyWordsVisibility,
    handleMessagePreviewClick,
  } = useExtendedMessage({
    form,
    socialType,
    disabled,
    placeholder,
    autoSize,
    selectedClientIds,
    formItemName,
    messageFieldStyles,
    onChange,
  });

  return (
    <div className={`${PREFIX}extended-message`}>
      <Space
        className="message-and-footer-space"
        direction="vertical"
        size="small"
      >
        {isMoreThanSymbolsMax && (
          <Alert
            className="alert-about-integration-type"
            type="info"
            showIcon
            message={
              'Обратите внимание, что, если последняя интеграция будет Инстаграмом*, ' +
              'текст сообщения обрежется на 998 символах. (* - признана экстремистской организацией в России.)'
            }
          />
        )}

        <div className="message-field-with-buttons">
          <MessageTemplatesWrapper
            withTemplateOffers={withTemplateOffers}
            form={form}
            formItemName={formItemName}
          >
            <Form.Item
              name={formItemName}
              className={cn('message-form-item', formItemClassName)}
              rules={formItemRules}
              style={{ margin: 0 }}
              validateTrigger={['onChange', 'onBlur']}
            >
              <TextArea
                ref={messageFieldRef}
                className={cn(
                  'message-field',
                  { 'small-message-text': messageTextSize === 'small' },
                  { 'message-templates-service': isMessageTemplatesService },
                  { [className]: !!className }
                )}
                style={messageFieldStyles}
                variant="borderless"
                maxLength={
                  socialType === LOYALTY_PROGRAM_SENDOUTS
                    ? undefined
                    : maxLength
                }
                {...props}
                data-testid={textAreaDataTestId}
              />
            </Form.Item>
          </MessageTemplatesWrapper>

          <Divider className="message-field-divider" />

          <Flex className="message-field-footer" justify="space-between">
            {isMoreThanSymbolsMax && (
              <Text className="counter" type="secondary">
                {messageSymbolsCount}/{maxLength}
              </Text>
            )}

            <div className="buttons-container">
              <Flex justify="end" align="center" gap={4}>
                <div ref={emojiVisibilityButtonRef}>
                  <Tooltip
                    title="Наборы эмоджи (реакций) для украшения сообщений"
                    placement="bottom"
                  >
                    <Button
                      type="link"
                      size="small"
                      id={'emoji-button'}
                      icon={<SmileOutlined id={'emoji-icon'} />}
                      onClick={toggleEmojiPicker}
                      data-testid={emojiButtonDataTestId}
                    />
                  </Tooltip>
                </div>

                <Tooltip
                  title="Список ключевых слов и способов формирования сообщения"
                  placement="bottom"
                >
                  <Button
                    className="key-words-icon"
                    type="link"
                    size="small"
                    onClick={handleKeyWordsVisibility}
                    data-testid={keywordsButtonDataTestId}
                  >
                    {'{}'}
                  </Button>
                </Tooltip>

                <Tooltip
                  title="Посмотреть, как выглядят версии отправляемого сообщения с учетом ключевых слов"
                  placement="bottomRight"
                >
                  <Button
                    type="link"
                    size="small"
                    onClick={handleMessagePreviewClick}
                    data-testid={previewButtonDataTestId}
                  >
                    Предпросмотр
                  </Button>
                </Tooltip>
              </Flex>
            </div>
          </Flex>
        </div>
      </Space>

      <KeyWords
        isKeyWordsOpen={isKeyWordsOpen}
        isKeywordsLoading={isKeywordsLoading}
        sendoutKeywords={sendoutKeywords}
        setIsKeyWordsOpen={setIsKeyWordsOpen}
        addExtraTextToMessage={addExtraTextToMessage}
        getDescriptionTag={getDescriptionTag}
      />

      {previewMessages?.length && (
        <MessagesPreviewWindow
          isMessagesPreviewModalOpen={isMessagesPreviewModalOpen}
          data={previewMessages}
          setIsMessagesPreviewModalOpen={setIsMessagesPreviewModalOpen}
        />
      )}

      {isEmojiOpen && (
        <div
          className={cn({ 'leads-service': isLeadsService })}
          ref={pickerRef}
        >
          <Picker onEmojiSelect={onEmojiSelect} locale="ru" theme="light" />
        </div>
      )}
    </div>
  );
};
